import { isRejectedWithValue, Middleware } from '@reduxjs/toolkit'
import { ePublicRoutes } from '../../routes/e-routes'
import { authApi } from '../../services/auth.service'
import { resetState } from '../reducers/authSlice'

const SESSION_REVOKED_MESSAGE = 'Token has been invalidated'

// Redux Toolkit types `action.payload` as `unknown` for rejected actions.
// This small type guard lets us safely read the HTTP status (RTK Query / baseQuery style).
const hasHttpStatus = (payload: unknown): payload is { status: number; data?: { message?: string } } => {
  return (
    typeof payload === 'object' && payload !== null && 'status' in payload && typeof (payload as { status?: unknown }).status === 'number'
  )
}

// @lat: [[auth#Session expiry]]
export const unauthenticatedMiddleware: Middleware =
  ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    // Redux middleware actions are typed as `unknown` in strict mode.
    // We keep this middleware minimal by casting once and validating what we read.
    const typedAction = action as any
    const currentPath = window.location.pathname
    const inPublicRoute = Object.values(ePublicRoutes).some((route) => {
      // Handle dynamic routes with parameters
      if (route.includes(':')) {
        const routePattern = route.replace(/:[^/]+/g, '[^/]+')
        return new RegExp(`^${routePattern}$`).test(currentPath)
      }
      return currentPath.includes(route)
    })

    const isOwnPwdError = typedAction.meta?.arg?.endpointName === 'updateOwnPassword'
    const isExtendSession = typedAction.meta?.arg?.endpointName === 'postExtendSession'
    const isAuthUpdating = getState()[authApi.reducerPath]?.isLoading // It verifies if the authApi is in loading state to prevent 401 error unecessary

    if (
      isRejectedWithValue(typedAction) &&
      hasHttpStatus(typedAction.payload) &&
      typedAction.payload.status === 401 &&
      !inPublicRoute &&
      !isOwnPwdError &&
      !isExtendSession
    ) {
      if (!isAuthUpdating) {
        dispatch(resetState())
        const isSessionRevoked = typedAction.payload.data?.message === SESSION_REVOKED_MESSAGE
        const reason = isSessionRevoked ? '?reason=sessionRevoked' : ''
        window.location.href = `/login${reason}`
      }
    }

    return next(action)
  }
